import 'ol/ol.css';

import Feature from 'ol/Feature.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import Map from 'ol/Map.js';
import View from 'ol/View.js';
import {
  Circle as CircleStyle, Fill, Icon, Stroke, Style, Text, RegularShape,
} from 'ol/style.js';
import {Cluster, Vector as VectorSource} from 'ol/source.js';
import {Point, Circle, Polygon} from 'ol/geom.js';
import {Tile as TileLayer, Vector as VectorLayer} from 'ol/layer.js';
import {fromLonLat} from 'ol/proj.js';
import OSM from 'ol/source/OSM.js';
import {createEmpty, extend, getHeight, getWidth} from 'ol/extent.js';
import Overlay from 'ol/Overlay.js';
import iconHeim from '../Images/haus-kl.gif'

import monotoneChainConvexHull from 'monotone-chain-convex-hull';

const innerCircleFillColor = 'rgba(247, 150, 30, 0.75)'
const convexHullFill = new Fill({
  color: 'rgba(154, 137, 112, 0.4)',
});
const convexHullStroke = new Stroke({
  color: 'rgba(204, 85, 0, 1)',
  width: 1.5,
});
const outerCircleFill = new Fill({
  color: 'rgba(255, 153, 102, 0.3)',
});
const innerCircleFill = new Fill({
  color: innerCircleFillColor,
});
const textFill = new Fill({
  color: '#fff',
});
const textStroke = new Stroke({
  color: 'rgba(0, 0, 0, 0.6)',
  width: 3,
});
const innerCircle = new CircleStyle({
  radius: 14,
  fill: innerCircleFill,
});
const outerCircle = new CircleStyle({
  radius: 20,
  fill: outerCircleFill,
});

/**
 * Single feature style, users for clusters with 1 feature and cluster circles.
 * @param {Feature} clusterMember A feature from a cluster.
 * @return {Style} An icon style for the cluster member's location.
 */
function clusterMemberStyle(clusterMember) {
  return [
    new Style({
      image: new Icon({
        src: iconHeim,
        color: 'black',
        scale: 0.25,
      }),
    })
  ];
}

let hoverFeature;

/**
 * Style for convex hulls of clusters, activated on hover.
 * @param {Feature} cluster The cluster feature.
 * @return {Style|null} Polygon style for the convex hull of the cluster.
 */
function clusterHullStyle(cluster) {
  if (cluster !== hoverFeature) {
    return null;
  }
  const originalFeatures = cluster.get('features');
  const points = originalFeatures.map((feature) => feature.getGeometry().getCoordinates(),);
  return new Style({
    geometry: new Polygon([
      monotoneChainConvexHull(points)
    ]),
    fill: convexHullFill,
    stroke: convexHullStroke,
  });
}

function clusterStyle(feature) {
  const size = feature.get('features').length;
  if (size > 1) {
    return [
      new Style({
        image: outerCircle,
      }),
      new Style({
        image: innerCircle,
        text: new Text({
          text: size.toString(),
          fill: textFill,
          stroke: textStroke,
        }),
      }),
    ];
  }
  const originalFeature = feature.get('features')[0];
  return clusterMemberStyle(originalFeature);
}

const source = new VectorSource({
  features: (new GeoJSON({featureProjection: 'EPSG:3857'})).readFeatures(document.getElementById("map").dataset.locations),
});

const clusterSource = new Cluster({
  distance: 40,
  minDistance: 0,
  source: source,
});

// Layer displaying the convex hull of the hovered cluster.
const clusterHulls = new VectorLayer({
  source: clusterSource,
  style: clusterHullStyle,
});

const clusters = new VectorLayer({
  source: clusterSource,
  style: clusterStyle,
});

const raster = new TileLayer({
  source: new OSM(),
});

const map = new Map({
  target: "map",
  layers: [raster, clusterHulls, clusters],
  view: new View({
    center: fromLonLat([10.00, 51.00]), // @TODO: Replace by <f:format.raw>{lat}</f:format.raw>, <f:format.raw>{lon}</f:format.raw
    zoom: 6 // @TODO: Replace by <f:format.raw>{zoom}</f:format.raw>,
  }),
  allOverlays: true,
});

let tooltipElement = document.getElementById("tooltip");
let tooltip = new Overlay({
  element: tooltipElement,
  positioning: 'bottom-center',
  stopEvent: false,
  offset: [0, -15],
});
map.addOverlay(tooltip);

map.on('pointermove', (event) => {
  clusters.getFeatures(event.pixel).then((features) => {
    if (features[0] !== hoverFeature) {
      // Display the convex hull on hover.
      hoverFeature = features[0];
      clusterHulls.setStyle(clusterHullStyle);
      // Change the cursor style to indicate that the cluster is clickable.
      map.getTargetElement().style.cursor = hoverFeature && hoverFeature.get('features').length > 1 ? 'pointer' : '';
    }
  });
});

let clickFeature, clickResolution;
map.on('click', (event) => {
  clusters.getFeatures(event.pixel).then((features) => {
    if (features.length > 0) {
      const clusterMembers = features[0].get('features');
      if (clusterMembers.length > 1) {
        // Calculate the extent of the cluster members.
        const extent = createEmpty();
        clusterMembers.forEach((feature) => extend(extent, feature.getGeometry().getExtent()),);
        const view = map.getView();
        const resolution = map.getView().getResolution();
        if (view.getZoom() === view.getMaxZoom() ||
          (getWidth(extent) < resolution && getHeight(extent) < resolution)
        ) {
          // Show an expanded view of the cluster members.
          clickFeature = features[0];
          clickResolution = resolution;
        } else {
          // Zoom to the extent of the cluster members.
          view.fit(extent, {duration: 500, padding: [50, 50, 50, 50]});
        }
      } else {
        let clickedFeatureInfo = features[0].get('features')[0]["values_"];
        let content = '<strong>' + clickedFeatureInfo['name'] + '</strong><br>' +
          clickedFeatureInfo['address'] + '<br>' +
          clickedFeatureInfo['zip'] + ' ' + clickedFeatureInfo['city'] + '<br>' +
          '<a href="' + clickedFeatureInfo['url'] + '">mehr Details...</a>'

        tooltip.setPosition(features[0].getGeometry().getCoordinates())
        $(tooltipElement).popover({
          placement: 'top',
          html: true,
          content: content,
        });
        tooltipElement.dataset.content = content;
        $(tooltipElement).popover('show');
      }
    } else {
      $(tooltipElement).popover('destroy');
    }
  });
});
